import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { CircleOff, Clock, MoreHorizontal, Pencil, Radio, UserRound } from 'lucide-react';

const menuButtonClass =
  'inline-flex items-center justify-center h-7 w-7 rounded-md text-content-secondary border border-slate-200 bg-white hover:bg-slate-50 hover:text-content-primary transition-colors';

const menuItemClass =
  'flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-content-primary hover:bg-slate-50 transition-colors';

const menuItemDangerClass =
  'flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors';

function AgentTableActionsMenu({ agentId, onDeactivate }) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuPosition = useCallback(() => {
    const button = buttonRef.current;
    const menu = menuRef.current;
    if (!button) {
      return;
    }

    const rect = button.getBoundingClientRect();
    const menuHeight = menu?.offsetHeight ?? 180;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < menuHeight + 8 && rect.top > menuHeight + 8;

    setMenuStyle({
      position: 'fixed',
      top: openUpward ? rect.top - menuHeight - 4 : rect.bottom + 4,
      right: Math.max(8, window.innerWidth - rect.right),
      visibility: 'visible',
      zIndex: 9999,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }

    updateMenuPosition();
    const frame = requestAnimationFrame(updateMenuPosition);

    const handleReposition = () => updateMenuPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      const inButton = buttonRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inButton && !inMenu) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const menuPanel = open ? (
    <div
      ref={menuRef}
      style={
        menuStyle ?? {
          position: 'fixed',
          top: -9999,
          left: 0,
          visibility: 'hidden',
        }
      }
      className="min-w-[11rem] rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
      role="menu"
    >
      <Link
        to={`/contacts?agentId=${agentId}`}
        onClick={() => setOpen(false)}
        className={menuItemClass}
        role="menuitem"
      >
        <UserRound className="h-3.5 w-3.5 shrink-0 text-content-muted" aria-hidden />
        Contatos
      </Link>
      <Link
        to={`/scheduled-messages?agentId=${agentId}`}
        onClick={() => setOpen(false)}
        className={menuItemClass}
        role="menuitem"
      >
        <Clock className="h-3.5 w-3.5 shrink-0 text-content-muted" aria-hidden />
        Mensagens agendadas
      </Link>
      <Link
        to={`/agents/${agentId}/edit`}
        onClick={() => setOpen(false)}
        className={menuItemClass}
        role="menuitem"
      >
        <Pencil className="h-3.5 w-3.5 shrink-0 text-content-muted" aria-hidden />
        Editar agente
      </Link>
      <Link
        to={`/channels?agentId=${agentId}`}
        onClick={() => setOpen(false)}
        className={menuItemClass}
        role="menuitem"
      >
        <Radio className="h-3.5 w-3.5 shrink-0 text-content-muted" aria-hidden />
        Canais
      </Link>
      {typeof onDeactivate === 'function' && (
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            onDeactivate();
          }}
          className={menuItemDangerClass}
          role="menuitem"
        >
          <CircleOff className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Desativar agente
        </button>
      )}
    </div>
  ) : null;

  return (
    <>
      <div className="inline-flex justify-end">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          title="Ações"
          aria-label="Ações do agente"
          aria-expanded={open}
          aria-haspopup="menu"
          className={menuButtonClass}
        >
          <MoreHorizontal className="h-4 w-4 shrink-0" aria-hidden />
        </button>
      </div>
      {open && createPortal(menuPanel, document.body)}
    </>
  );
}

export default AgentTableActionsMenu;
